import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Select2Multiple from 'v-select2-multiple-component';
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import { required, minValue } from "vuelidate/lib/validators";
import Loading from 'vue-loading-overlay';
import axios from 'axios';
import CONFIG from "@/assets/js/config.custom.js";
import ValidationService from '@/services/ValidationService';

export default {
  name: "attendanceTeacherList",
  components: {
    MainLayout,
    flatPickr,
    Select2Multiple,
    Loading,
  },

  mixins: [ValidationService],

  data() {
    return {
      isLoading: false,
      submitted: false,
      disabled: false,
      isSearch: false,
      searchDisabled: false,
      dateError: "",
      teacherArr: [
        {id: 0, text: "Select Employee"}
      ],
      searchData: {
        employeeId: 0,
        startDate: "",
        endDate: "",
      },
      attendanceList: [],
      deletedIds: [],
      total: 0,
      emptyData: CONFIG.FORM_REQUEST_MESSAGE,
      configDateTime: {
        allowInput: true,
        enableTime: true,
        timeFormat: "h:i K", 
        noCalendar: true, 
        time_24hr: false,
        enableSeconds: false,
        altInput: true,
      },
      attendanceError: [],
    };
  },

  validations() {
    const searchData = {
      startDate: { required },
      endDate: { required },
    };

    if (this.isHrOperator) {
      searchData.employeeId = {
        required,
        minValue: minValue(1)
      };
    }

    return { searchData };
  },

  computed: {
    isHrOperator() {
      const role = Number(this.$store.state.user?.role);
      return role === CONFIG.ROLE.ID.HR_OPERATOR || role === CONFIG.ROLE.ID.SUPER_ADMIN;
    },
    attendanceApiBase() {
      return this.isHrOperator ? 'hr/employee-attendance' : 'employee-attendance';
    },
    validDates() {
      const startDate = this.searchData.startDate;
      const endDate = this.searchData.endDate;

      if (!startDate || !endDate) {
        return false;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (end < start) {
        this.dateError = 'End date must be equal or after start date';
        return false;
      }

      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      if (diffDays > 31) {
        this.dateError = 'Date range cannot exceed 31 days';
        return false;
      }

      return true;
    }
  },

  methods: {
    getEmployeeNameList() {
      axios.get("/employee/list", {
        params: {
          exclude_admin_hr: true,
        },
      })
        .then((response) => {
          const employees = response.data.data || [];
          if (employees.length > 0) {
            this.teacherArr = [{ id: 0, text: "Select Employee" }].concat(
              employees.map((emp) => ({
                id: emp.id,
                text: `${emp.employee_code} - ${emp.name} (${emp.employeeable_type_text})`,
              }))
            );
          }
        })
        .catch((error) => {
          if (error.response && error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
        })
        .finally(() => this.isLoading = false);
    },

    getAttendanceList() {
      this.searchDisabled = true;
      this.isLoading = true;
      this.isSearch = true;

      if (this.$v.$invalid || !this.validDates) {
        this.searchDisabled = false;
        this.isLoading = false;
        return;
      }

      this.attendanceList = [];
      this.deletedIds = [];

      const request = {
        start_date: this.searchData.startDate,
        end_date: this.searchData.endDate
      };
      const url = this.isHrOperator
        ? `/${this.attendanceApiBase}/employee/${this.searchData.employeeId}`
        : `/${this.attendanceApiBase}/employee`;

      axios.get(url, {params: request})
        .then(res => {
          const attendances = res.data;
          this.total = attendances.length;
          this.attendanceList = this.extractAttandanceData(attendances, CONFIG.SHIFT_LIMIT);
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          console.log("Teacher Attendance Search API Error", error);
        })
        .finally(() => {
          this.isLoading = false;
          this.isSearch = false;
          this.searchDisabled = false;
          this.disabled = Boolean(!this.total);
          this.emptyData = CONFIG.NO_SHIFT_DATA;
        });
    },

    extractAttandanceData(attendances, limit) {
      if (!attendances.length) {
        return [];
      }

      const filteredAttendances = attendances.filter((attendance) => {
        if (!this.isHrOperator && (attendance.is_calculated || attendance.is_calculated === 1)) {
          return false;
        }
        return true;
      });

      if (!filteredAttendances.length) {
        return [];
      }

      return filteredAttendances.map((attendance) => {
        const result = [];
        let rowSpans = 0;

        for (let i = 1; i <= limit; i ++) {
          if (!attendance[`has_shift${i}`]) {
            continue;
          }

          const data = {
            shiftSection: i,
            id: attendance.id,
            arrival: attendance[`actual_arrival_time${i}`],
            dept:  attendance[`actual_dept_time${i}`]
          }

          if (!result.length) {
            data["isRow"] = true;
            data["date"] = attendance.date;
            data["remark"] = attendance.remark;
          } 

          result.push(data)
          rowSpans ++;
        }
        result[0].rowSpans = rowSpans;

        return result;
      }).flat();
    },

    compressAttandanceData(attendances) {
      let list = [];
      const padSec = (time) => time.split(":").length < 3 ? time + ":00" : time;

      for (let i = 0;i < attendances.length; i ++) {
        const item = attendances[i];

        if (this.deletedIds.includes(item.id)) continue;

        const startTime = item.arrival ? padSec(item.arrival) : null;
        const endTime = item.dept ? padSec(item.dept) : null;

        if (item.isRow) {
          const attendance = {
            id: item.id,
            remark: item.remark
          };

          attendance.shiftSection = item.shiftSection;
          attendance[`actual_arrival_time${item.shiftSection}`] = startTime;
          attendance[`actual_dept_time${item.shiftSection}`] = endTime;
          list.push(attendance);
        } else {
          list[list.length - 1].shiftSection = item.shiftSection;
          list[list.length - 1][`actual_arrival_time${item.shiftSection}`] = startTime;
          list[list.length - 1][`actual_dept_time${item.shiftSection}`] = endTime;
        }
      }

      return list;
    },

    register() {
      this.submitted = true;
      this.disabled = true;
      this.validationErrors = {};
      this.attendanceError = [];
      let attendanceArr = this.compressAttandanceData(this.attendanceList);

      axios.patch(`/${this.attendanceApiBase}/register`, {
        attendances: attendanceArr
      })
      .then(() => {
        this.$notification.success("Attendance for Employee Added Successfully", {
          timer: 3,
          position: "bottomCenter",
        });
        this.showList();
      })
      .catch(error => {
        if (error.response && error.response.status === 422) {
          this.validationErrors = error.response.data.errors;
          this.getDateErrorMessage(attendanceArr);
          if (this.attendanceList.length === 0) {
            this.$notification.error("Please search attendance data first.", {
              timer: 3,
              position: "bottomCenter",
            });
          }
        } else if (error.response && error.response.status == 401) {
          this.$store.commit('logout');
          window.location.replace('login');
        } else {
          this.printValidationError(error.response);
        }

        this.isLoading = false;
        this.disabled = false;
        console.log("Attendance for Employee Create API Error", error.errors);
      });
    },

    showList() {
      this.$router.push({
        path: "/employee-attendance/list"
      });
    },

    confirmDelete(id) {
      if (window.confirm("Are you sure you want to delete?")) {
        this.deletedIds.push(id);
        this.total = this.total - 1;
        this.disabled = Boolean(!this.total);
      }
    },

    getDateErrorMessage(attendanceArr) {
      Object.entries(this.validationErrors)
        .filter(([key]) => /^attendances\.\d+\.actual_(arrival|dept)_time\d*$/.test(key))
        .forEach(([key, messages]) => {
          const fitltersMsgs = messages.filter(msg => !msg.includes("attendances"));
          const [, index, field] = key.split('.');
          const attendance = attendanceArr?.[Number(index)];
          if (!attendance) return;

          const shift = Number((field.match(/\d+$/) || [0])[0]);
          const type = key.includes("arrival") ? "arrival" : "dept";

          this.attendanceError[attendance.id] = this.attendanceError[attendance.id] || [];
          this.attendanceError[attendance.id][shift] = this.attendanceError[attendance.id][shift] || {};
          this.attendanceError[attendance.id][shift][type] = fitltersMsgs[0];
        });
    },

    printDateErrorMessage(id, shift, type) {
      try {
        const message = this.attendanceError[id][shift][type];
        return message;
      } catch (err) {
        return false;
      }
    }
  },

  mounted() {
    if (this.isHrOperator) {
      this.isLoading = true;
      this.getEmployeeNameList();
    } else {
      this.isLoading = false;
    }
  },
};
